import { nomeDoSistema } from '@/lib/sistemaDaClinica';
import { useMemo, useState } from 'react';
import { useAuth } from '@/auth/AuthProvider';
import { useLeads } from '@/leads/LeadsProvider';
import { formaPorId } from '@/leads/recebimentos';
import {
  confirmarImportacao,
  novoIdImportacao,
  previewImportacao,
  subirPlanilha,
} from '@/services/importacaoService';

/**
 * Importação do "Contas a receber" do Careflow (VA-2).
 *
 * A tela NÃO lê a planilha: sobe o arquivo e mostra o que o SERVIDOR entendeu.
 * Quem importa é o SDR, que ganha comissão sobre o que for gravado — o valor
 * precisa vir do arquivo, não de um payload montado aqui. Por isso nada de
 * valor, data ou forma é editável: a única coisa que a pessoa decide é PARA QUAL
 * LEAD vai cada parcela.
 */

const moeda = (v) =>
  Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dataCurta = (v) => {
  const d = v?._seconds ? new Date(v._seconds * 1000) : new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

function LinhaParcela({ p, fechado = false, children }) {
  return (
    <tr className={fechado ? 'linha-apagada' : undefined}>
      <td>
        {p.nomePaciente}
        {p.codigoPaciente && <span className="canal-instancia">{p.codigoPaciente}</span>}
        {/* ★★ Travas do fechamento (item 1): a parcela é de um mês já fechado. */}
        {fechado && <span className="campo-ajuda"> mês fechado — não entra em folha</span>}
      </td>
      <td>{dataCurta(p.data)}</td>
      <td>{p.parcela || '—'}</td>
      <td>{formaPorId(p.forma).label}</td>
      <td className="valor">
        {moeda(p.valor)}
        {/* O desconto SAI da base (é dinheiro que a pessoa não pagou) e a multa
            também fica de fora (é custo do atraso). Os dois aparecem para
            ninguém descobrir a diferença meses depois. */}
        {p.desconto > 0 && <span className="campo-ajuda">bruto {moeda(p.bruto)} − desc. {moeda(p.desconto)}</span>}
        {p.multa > 0 && <span className="campo-ajuda">multa {moeda(p.multa)} fora</span>}
      </td>
      {children}
    </tr>
  );
}

export default function ImportarRecebimentosPage() {
  const { clinicaId } = useAuth();
  const { leads, clinica } = useLeads();
  const sistema = nomeDoSistema(clinica?.config);

  const [arquivo, setArquivo] = useState(null);
  const [importacaoId, setImportacaoId] = useState(null);
  const [nomeArquivo, setNomeArquivo] = useState(null);
  const [preview, setPreview] = useState(null);
  const [escolhas, setEscolhas] = useState({}); // idExterno -> leadId ('' = não importar)
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState(null);
  const [resultado, setResultado] = useState(null);
  /**
   * ★★ Travas do fechamento (03/11/2026, item 1) — parcela de mês FECHADO.
   * O padrão é NÃO importar: essas linhas vêm desmarcadas e travadas. Marcar esta caixa é a
   * confirmação explícita, e o servidor a registra na importação.
   */
  const [importarFechado, setImportarFechado] = useState(false);
  const idsDeMesFechado = useMemo(() => new Set(preview?.mesFechado?.ids ?? []), [preview]);

  const leadsOrdenados = useMemo(
    () => [...(leads ?? [])].sort((a, b) => (a.nome ?? '').localeCompare(b.nome ?? '')),
    [leads],
  );

  const confirmadas = useMemo(
    () => Object.entries(escolhas)
      .filter(([, leadId]) => leadId)
      .filter(([idExterno]) => importarFechado || !idsDeMesFechado.has(idExterno)),
    [escolhas, importarFechado, idsDeMesFechado],
  );

  const totalAImportar = useMemo(() => {
    if (!preview) return 0;
    const porId = new Map(
      [...preview.automaticas, ...preview.paraConfirmar].map((p) => [p.idExterno, p]),
    );
    return confirmadas.reduce((t, [idExterno]) => t + (porId.get(idExterno)?.valor ?? 0), 0);
  }, [preview, confirmadas]);

  async function conferir(evento) {
    evento.preventDefault();
    if (!arquivo) return;
    setErro(null);
    setResultado(null);
    setOcupado(true);
    try {
      const id = novoIdImportacao(clinicaId);
      const nome = await subirPlanilha(clinicaId, id, arquivo);
      const dados = await previewImportacao(clinicaId, id, nome);
      setImportacaoId(id);
      setNomeArquivo(nome);
      setPreview(dados);
      setImportarFechado(false);
      // As automáticas já vêm marcadas; as de confirmar, não. É o SDR que decide
      // cada uma delas — nome nunca casa sozinho.
      // ★★ Item 1: MENOS as de mês fechado, que nascem desmarcadas.
      const fechadas = new Set(dados.mesFechado?.ids ?? []);
      setEscolhas(Object.fromEntries(
        dados.automaticas.filter((p) => !fechadas.has(p.idExterno)).map((p) => [p.idExterno, p.leadId]),
      ));
    } catch (e) {
      setErro(e.message ?? 'Não foi possível ler a planilha.');
    } finally {
      setOcupado(false);
    }
  }

  async function confirmar() {
    setErro(null);
    setOcupado(true);
    try {
      const decisoes = confirmadas.map(([idExterno, leadId]) => ({ idExterno, leadId }));
      const fechadasMarcadas = decisoes.filter((d) => idsDeMesFechado.has(d.idExterno)).length;
      if (importarFechado && fechadasMarcadas > 0) {
        const avisos = (preview?.mesFechado?.porMes ?? []).map((m) => m.mensagem).join('\n');
        // ⚠ Segunda confirmação, de propósito: é o único caminho em que dinheiro entra no banco
        // sabendo que não vai para folha nenhuma.
        if (!window.confirm(`${avisos}\n\nImportar mesmo assim ${fechadasMarcadas} parcela(s) de mês fechado? Isso fica registrado.`)) {
          return;
        }
      }
      const dados = await confirmarImportacao(clinicaId, importacaoId, nomeArquivo, decisoes, {
        importarMesFechado: importarFechado && fechadasMarcadas > 0,
      });
      setResultado(dados);
      setPreview(null);
      setEscolhas({});
      setArquivo(null);
    } catch (e) {
      setErro(e.message ?? 'Não foi possível confirmar a importação.');
    } finally {
      setOcupado(false);
    }
  }

  const c = preview?.contadores;

  return (
    <section className="pagina">
      <header className="pagina-cabecalho">
        <h1>Importar recebimentos do {sistema}</h1>
      </header>

      <p className="secao-sub">
        Exporte o relatório <strong>Contas a receber</strong> do {sistema} em Excel e escolha o
        arquivo aqui. O sistema lê a planilha no servidor e mostra o que entendeu — valores,
        datas e formas de pagamento vêm do arquivo e não podem ser alterados nesta tela. Você
        só confirma <strong>a qual lead</strong> cada pagamento pertence.
      </p>

      <form className="formulario cartao" onSubmit={conferir}>
        <div className="campo">
          <label htmlFor="ir-arquivo">Arquivo do {sistema} (.xlsx)</label>
          <input
            id="ir-arquivo"
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            disabled={ocupado}
            onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
          />
        </div>
        <button type="submit" className="btn-primario" disabled={!arquivo || ocupado}>
          {ocupado ? 'Lendo…' : 'Conferir'}
        </button>
      </form>

      {erro && <p className="erro">{erro}</p>}

      {resultado && (
        <div className="cartao">
          <h2 className="secao-titulo">Importação concluída</h2>
          <p>
            <strong>{resultado.gravadas}</strong> pagamento
            {resultado.gravadas === 1 ? '' : 's'} lançado
            {resultado.gravadas === 1 ? '' : 's'}, somando{' '}
            <strong>{moeda(resultado.valorGravado)}</strong> em {resultado.leadsAfetados} lead
            {resultado.leadsAfetados === 1 ? '' : 's'}.
          </p>
          {resultado.ignoradas?.mesFechado > 0 && (
            <p className="aviso">
              {resultado.ignoradas.mesFechado} parcela(s) de mês já fechado ficaram de fora, como manda o
              padrão. Para incluí-las, reabra o mês e importe o arquivo de novo.
            </p>
          )}
          {resultado.codigosGravados > 0 && (
            <p className="campo-ajuda">
              {resultado.codigosGravados} lead{resultado.codigosGravados === 1 ? '' : 's'}{' '}
              ganhou o código do {sistema} — nas próximas importações eles casam sozinhos.
            </p>
          )}
        </div>
      )}

      {preview && (
        <>
          <div className="indicadores">
            <div className="indicador">
              <span className="indicador-rotulo">Linhas no arquivo</span>
              <span className="indicador-valor">{c.linhas}</span>
              <span className="indicador-nota">{moeda(preview.total)} no total</span>
            </div>
            <div className="indicador">
              <span className="indicador-rotulo">Casadas pelo código</span>
              <span className="indicador-valor verde">{c.casadasAuto}</span>
              <span className="indicador-nota">{moeda(c.valorAutomaticas)}</span>
            </div>
            <div className="indicador">
              <span className="indicador-rotulo">Precisam de confirmação</span>
              <span className="indicador-valor">{c.paraConfirmar}</span>
              <span className="indicador-nota">{moeda(c.valorParaConfirmar)}</span>
            </div>
            <div className="indicador">
              <span className="indicador-rotulo">Já importadas antes</span>
              <span className="indicador-valor">{c.jaImportadas}</span>
              <span className="indicador-nota">não serão duplicadas</span>
            </div>
            <div className="indicador">
              <span className="indicador-rotulo">Sem lead correspondente</span>
              <span className="indicador-valor">{c.semLead}</span>
              <span className="indicador-nota">{moeda(c.valorSemLead)}</span>
            </div>
          </div>

          {preview.mesFechado?.parcelas > 0 && (
            <div className="aviso" role="alert">
              {preview.mesFechado.porMes.map((m) => <p key={m.mes}>{m.mensagem}</p>)}
              <label className="linha">
                <input
                  type="checkbox"
                  checked={importarFechado}
                  onChange={(e) => setImportarFechado(e.target.checked)}
                />
                Importar mesmo assim as parcelas de mês fechado (fica registrado)
              </label>
            </div>
          )}

          {preview.automaticas.length > 0 && (
            <section className="secao">
              <h2 className="secao-titulo">Casadas automaticamente ({preview.automaticas.length})</h2>
              <p className="secao-sub">
                O código do paciente no {sistema} bate com o do lead. Desmarque se algo estiver
                errado.
              </p>
              <div className="tabela-envolve">
                <table className="tabela">
                  <thead>
                    <tr>
                      <th>Paciente</th><th>Data</th><th>Parcela</th><th>Forma</th>
                      <th>Valor</th><th>Lead</th><th>Importar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.automaticas.map((p) => (
                      <LinhaParcela key={p.idExterno} p={p} fechado={idsDeMesFechado.has(p.idExterno)}>
                        <td>{p.lead?.nome}</td>
                        <td>
                          <input
                            type="checkbox"
                            disabled={idsDeMesFechado.has(p.idExterno) && !importarFechado}
                            checked={Boolean(escolhas[p.idExterno]) && (importarFechado || !idsDeMesFechado.has(p.idExterno))}
                            onChange={(e) =>
                              setEscolhas((a) => ({ ...a, [p.idExterno]: e.target.checked ? p.leadId : '' }))
                            }
                          />
                        </td>
                      </LinhaParcela>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {preview.paraConfirmar.length > 0 && (
            <section className="secao">
              <h2 className="secao-titulo">Precisam da sua confirmação ({preview.paraConfirmar.length})</h2>
              <p className="secao-sub">
                O nome bate, mas o código não foi preenchido — ou há mais de um candidato.
                <strong> Nome igual não é a mesma pessoa</strong>: confira antes de marcar. Ao
                confirmar, o código do {sistema} é gravado no lead e a próxima importação casa
                sozinha.
              </p>
              <div className="tabela-envolve">
                <table className="tabela">
                  <thead>
                    <tr>
                      <th>Paciente</th><th>Data</th><th>Parcela</th><th>Forma</th>
                      <th>Valor</th><th>Lead</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.paraConfirmar.map((p) => (
                      <LinhaParcela key={p.idExterno} p={p} fechado={idsDeMesFechado.has(p.idExterno)}>
                        <td>
                          <select
                            disabled={idsDeMesFechado.has(p.idExterno) && !importarFechado}
                            value={(importarFechado || !idsDeMesFechado.has(p.idExterno)) ? (escolhas[p.idExterno] ?? '') : ''}
                            onChange={(e) =>
                              setEscolhas((a) => ({ ...a, [p.idExterno]: e.target.value }))
                            }
                          >
                            <option value="">Não importar</option>
                            {(p.candidatos ?? []).map((cand) => (
                              <option key={cand.id} value={cand.id}>
                                {cand.nome} — sugerido
                              </option>
                            ))}
                            <option disabled>──────────</option>
                            {leadsOrdenados.map((l) => (
                              <option key={l.id} value={l.id}>{l.nome}</option>
                            ))}
                          </select>
                        </td>
                      </LinhaParcela>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {preview.semLead.length > 0 && (
            <section className="secao">
              <h2 className="secao-titulo">Sem lead correspondente ({preview.semLead.length})</h2>
              {/* Este texto existe para a pessoa não ler o resultado como falha.
                  A clínica atende há anos; o funil de WhatsApp é recente. */}
              <p className="secao-sub">
                <strong>Isto é o esperado, não um erro.</strong> São pacientes que não vieram
                pelo funil de WhatsApp — a maioria do relatório costuma cair aqui. Eles não
                entram em comissão nenhuma e são simplesmente ignorados.
              </p>
              <details>
                <summary>Ver as {preview.semLead.length} linhas</summary>
                <div className="tabela-envolve">
                  <table className="tabela">
                    <thead>
                      <tr><th>Paciente</th><th>Data</th><th>Parcela</th><th>Forma</th><th>Valor</th></tr>
                    </thead>
                    <tbody>
                      {preview.semLead.map((p) => <LinhaParcela key={p.idExterno} p={p} />)}
                    </tbody>
                  </table>
                </div>
              </details>
            </section>
          )}

          {preview.jaImportadas.length > 0 && (
            <section className="secao">
              <h2 className="secao-titulo">Já importadas antes ({preview.jaImportadas.length})</h2>
              <p className="secao-sub">
                Reconhecidas pelo identificador da parcela no {sistema}. Importar o mesmo arquivo
                de novo não duplica nada.
              </p>
            </section>
          )}

          <footer className="dialogo-acoes">
            <span className="secao-sub">
              {confirmadas.length} linha{confirmadas.length === 1 ? '' : 's'} marcada
              {confirmadas.length === 1 ? '' : 's'} · {moeda(totalAImportar)}
            </span>
            <button
              type="button"
              className="btn-primario"
              disabled={ocupado || confirmadas.length === 0}
              onClick={confirmar}
            >
              {ocupado ? 'Importando…' : `Importar ${confirmadas.length} pagamento(s)`}
            </button>
          </footer>
        </>
      )}
    </section>
  );
}
